import React from 'react';
import {
  FileCode,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  DollarSign,
  Shield,
  GitBranch,
  Play,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  MessageSquare,
  History,
  Eye,
  Code2,
  RefreshCw,
  TestTube
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/**
 * TaskPanel - Right sidebar showing task details and controls
 */
const TaskPanel = ({
  task,
  onAction,
  disabled = false
}) => {
  if (!task) {
    return (
      <div className="h-full flex items-center justify-center p-6 bg-[rgba(10,38,28,0.4)] border-l border-[rgba(142,218,182,0.1)]">
        <div className="text-center">
          <Code2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-500 text-sm">Select a task to view details</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      inspecting: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      plan_ready: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      patch_ready: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      executed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
      rolled_back: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
    };
    return colors[status] || colors.planning;
  };

  const getRiskColor = (risk) => {
    const colors = {
      low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      medium: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      high: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      critical: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[risk?.toLowerCase()] || colors.medium;
  };

  const getTypeColor = (type) => {
    const colors = {
      ui: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      bug: 'bg-red-500/20 text-red-300 border-red-500/30',
      backend: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      feature: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      refactor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
    };
    return colors[type?.toLowerCase()] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  return (
    <div className="h-full overflow-y-auto bg-[rgba(10,38,28,0.4)] border-l border-[rgba(142,218,182,0.1)] p-4 space-y-4">
      {/* Task Header */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">{task.title || 'Current Task'}</h3>
        <div className="flex flex-wrap gap-2">
          <Badge className={`${getStatusColor(task.status)} border`}>
            {task.status?.replace('_', ' ') || 'Planning'}
          </Badge>
          {task.type && (
            <Badge className={`${getTypeColor(task.type)} border`}>
              {task.type}
            </Badge>
          )}
        </div>
      </div>

      {/* Risk Level */}
      {task.risk && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
              <Shield className="w-3.5 h-3.5" />
              Risk Level
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <Badge className={`${getRiskColor(task.risk)} border`}>
              {task.risk}
            </Badge>
            {task.riskReason && (
              <p className="text-xs text-slate-500 mt-2">{task.riskReason}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Files Affected */}
      {task.files && task.files.length > 0 && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
              <FileCode className="w-3.5 h-3.5" />
              Files Affected
              <Badge className="bg-slate-700 text-slate-300 border-0 text-[10px]">
                {task.files.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {task.files.map((file, idx) => (
                <div key={idx} className="text-xs font-mono text-slate-400 truncate bg-slate-900/50 px-2 py-1 rounded">
                  {file.path || file}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Token/Cost Estimate */}
      {(task.tokens || task.cost) && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
              <Zap className="w-3.5 h-3.5" />
              Estimated Usage
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-900/50 p-2 rounded">
                <p className="text-[10px] text-slate-500">Tokens</p>
                <p className="text-sm font-medium text-amber-300">{task.tokens?.toLocaleString() || '-'}</p>
              </div>
              <div className="bg-slate-900/50 p-2 rounded">
                <p className="text-[10px] text-slate-500">Est. Cost</p>
                <p className="text-sm font-medium text-emerald-300">${task.cost?.toFixed(4) || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Status */}
      {task.planStatus && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
              <CheckCircle className="w-3.5 h-3.5" />
              Plan Status
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Classification</span>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-0 text-[10px]">
                  <CheckCircle className="w-2.5 h-2.5 mr-1" />
                  Done
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">File Inspection</span>
                <Badge className={task.planStatus.inspection === 'done' 
                  ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]"
                  : "bg-slate-500/20 text-slate-400 border-0 text-[10px]"}>
                  {task.planStatus.inspection === 'done' ? (
                    <><CheckCircle className="w-2.5 h-2.5 mr-1" />Done</>
                  ) : (
                    <><Clock className="w-2.5 h-2.5 mr-1" />Pending</>
                  )}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Plan Generation</span>
                <Badge className={task.planStatus.plan === 'done' 
                  ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]"
                  : "bg-slate-500/20 text-slate-400 border-0 text-[10px]"}>
                  {task.planStatus.plan === 'done' ? (
                    <><CheckCircle className="w-2.5 h-2.5 mr-1" />Done</>
                  ) : (
                    <><Clock className="w-2.5 h-2.5 mr-1" />Pending</>
                  )}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Admin Approval</span>
                <Badge className={task.planStatus.approval === 'approved' 
                  ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]"
                  : task.planStatus.approval === 'rejected'
                    ? "bg-red-500/20 text-red-300 border-0 text-[10px]"
                    : "bg-slate-500/20 text-slate-400 border-0 text-[10px]"}>
                  {task.planStatus.approval === 'approved' ? (
                    <><CheckCircle className="w-2.5 h-2.5 mr-1" />Approved</>
                  ) : task.planStatus.approval === 'rejected' ? (
                    <><XCircle className="w-2.5 h-2.5 mr-1" />Rejected</>
                  ) : (
                    <><Clock className="w-2.5 h-2.5 mr-1" />Pending</>
                  )}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Patch Status */}
      {task.patchStatus && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
              <GitBranch className="w-3.5 h-3.5" />
              Patch Status
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Generated</span>
                <Badge className={task.patchStatus.generated 
                  ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]"
                  : "bg-slate-500/20 text-slate-400 border-0 text-[10px]"}>
                  {task.patchStatus.generated ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Tests</span>
                <Badge className={task.patchStatus.tests === 'passed' 
                  ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]"
                  : task.patchStatus.tests === 'failed'
                    ? "bg-red-500/20 text-red-300 border-0 text-[10px]"
                    : "bg-slate-500/20 text-slate-400 border-0 text-[10px]"}>
                  {task.patchStatus.tests || 'Pending'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Deployed</span>
                <Badge className={task.patchStatus.deployed 
                  ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]"
                  : "bg-slate-500/20 text-slate-400 border-0 text-[10px]"}>
                  {task.patchStatus.deployed ? 'Yes' : 'No'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3 space-y-2">
          {/* Inspect Files */}
          <Button
            onClick={() => onAction?.('inspect_files')}
            disabled={disabled || task.status === 'rejected'}
            variant="outline"
            size="sm"
            className="w-full justify-start border-slate-600 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300"
          >
            <Eye className="w-4 h-4 mr-2" />
            Inspect Files
          </Button>

          {/* Generate Plan */}
          <Button
            onClick={() => onAction?.('generate_plan')}
            disabled={disabled || task.status === 'rejected'}
            variant="outline"
            size="sm"
            className="w-full justify-start border-slate-600 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Generate Plan
          </Button>

          {/* Approve Plan */}
          {task.status === 'plan_ready' && (
            <Button
              onClick={() => onAction?.('approve_plan')}
              disabled={disabled}
              className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Approve Plan
            </Button>
          )}

          {/* Generate Patch */}
          {(task.status === 'approved' || task.status === 'plan_ready') && (
            <Button
              onClick={() => onAction?.('generate_patch')}
              disabled={disabled || task.status !== 'approved'}
              variant="outline"
              size="sm"
              className="w-full justify-start border-blue-500/50 text-blue-300 hover:bg-blue-500/10 disabled:opacity-50"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              Generate Patch
            </Button>
          )}

          {/* Run Tests */}
          {task.status === 'patch_ready' && (
            <Button
              onClick={() => onAction?.('run_tests')}
              disabled={disabled}
              variant="outline"
              size="sm"
              className="w-full justify-start border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Run Tests
            </Button>
          )}

          {/* Show Diff */}
          {task.patch && (
            <Button
              onClick={() => onAction?.('show_diff')}
              disabled={disabled}
              variant="outline"
              size="sm"
              className="w-full justify-start border-slate-600 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300"
            >
              <Code2 className="w-4 h-4 mr-2" />
              Show Diff
            </Button>
          )}

          {/* Request Revision */}
          {(task.status === 'plan_ready' || task.status === 'patch_ready') && (
            <Button
              onClick={() => onAction?.('request_revision')}
              disabled={disabled}
              variant="outline"
              size="sm"
              className="w-full justify-start border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Request Revision
            </Button>
          )}

          {/* Approve Deploy */}
          {task.status === 'patch_ready' && (
            <Button
              onClick={() => onAction?.('approve_deploy')}
              disabled={disabled}
              className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Approve & Deploy
            </Button>
          )}

          {/* Rollback */}
          {task.status === 'executed' && (
            <Button
              onClick={() => onAction?.('rollback')}
              disabled={disabled}
              variant="outline"
              size="sm"
              className="w-full justify-start border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Rollback
            </Button>
          )}

          {/* Reject */}
          {task.status !== 'rejected' && task.status !== 'executed' && (
            <Button
              onClick={() => onAction?.('reject')}
              disabled={disabled}
              variant="outline"
              size="sm"
              className="w-full justify-start border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Reject
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Audit Timeline */}
      {task.audit && task.audit.length > 0 && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-2 text-slate-400">
              <History className="w-3.5 h-3.5" />
              Audit Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {task.audit.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-slate-300">{entry.action}</p>
                    <p className="text-slate-600">
                      {entry.user} - {entry.timestamp && new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Notice */}
      <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/50">
        <div className="flex items-start gap-2">
          <Shield className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-slate-400">Security Gate</p>
            <p className="text-[10px] text-slate-600 mt-0.5">
              All actions are logged. Secrets are redacted. No auto-deploy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskPanel;
